// feat_407 — Banco de conocimiento (global, sin datos personales). Lectura: todo el staff.
// Escritura (editar / aprobar sugerido / desactivar / nueva): solo master.
import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Switch, TextField, Typography,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import { Clamp, errorText, fmtDateTime } from "./shared";
import useDebouncedValue from "../../hooks/useDebouncedValue";

const ST = { sugerido: "#f57c00", aprobado: "#2e7d32", desactivado: "#757575" };
const STATUS_OPTIONS = [
  ["", "Todos"],
  ["sugerido", "Sugeridos (sin revisar)"],
  ["aprobado", "Aprobados"],
  ["desactivado", "Desactivados"],
];

const EditDialog = ({ entry, onClose, onSaved }) => {
  const [f, setF] = useState({
    topic: entry?.topic || "",
    title: entry?.title || "",
    answer: entry?.answer || "",
    language: entry?.language || "es",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (entry) await api.put(`/superadmin/support/knowledge/${entry.id}`, { ...f, requiresReview: false });
      else await api.post(`/superadmin/support/knowledge`, f);
      toast.success("Guardado. Se le sacan datos personales al guardar.");
      onSaved();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{entry ? `Editar entrada #${entry.id}` : "Nueva entrada del banco"}</DialogTitle>
      <DialogContent>
        <TextField label="Tema" fullWidth variant="outlined" margin="dense" value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} inputProps={{ maxLength: 120 }} />
        <TextField label="Título" fullWidth variant="outlined" margin="dense" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} inputProps={{ maxLength: 200 }} />
        <TextField label="Respuesta" fullWidth variant="outlined" margin="dense" multiline minRows={6} value={f.answer} onChange={(e) => setF({ ...f, answer: e.target.value })} inputProps={{ maxLength: 8000 }} />
        <TextField select label="Idioma" variant="outlined" margin="dense" value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })} style={{ minWidth: 140 }}>
          {["es", "it", "en", "pt"].map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
        </TextField>
        <Typography variant="caption" color="textSecondary" component="div">
          No escribas nombres de clientes, teléfonos ni mails: el banco alimenta las respuestas a TODAS las empresas.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" disabled={busy || !f.topic.trim() || !f.title.trim() || !f.answer.trim()} onClick={save}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

const KnowledgeTab = ({ master, onOpenRequest }) => {
  const [status, setStatus] = useState("");
  const [review, setReview] = useState(false);
  const [search, setSearch] = useState("");
  const q = useDebouncedValue(search, 300);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState(undefined);

  const load = useCallback(async (p = 1) => {
    try {
      const { data: d } = await api.get("/superadmin/support/knowledge", { params: { status: status || undefined, search: q || undefined, requiresReview: review || undefined, page: p } });
      setData((prev) => (p > 1 && prev ? { ...d, entries: [...prev.entries, ...d.entries] } : d));
      setPage(p);
    } catch (err) {
      toast.error(errorText(err));
    }
  }, [status, q, review]);

  useEffect(() => { load(1); }, [load]);

  const setEntryStatus = async (e, s) => {
    try {
      await api.put(`/superadmin/support/knowledge/${e.id}`, { status: s });
      toast.success(s === "aprobado" ? "Aprobada." : s === "desactivado" ? "Desactivada." : "Actualizada.");
      load(1);
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const t = data?.totals || {};
  return (
    <Box>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
        {t.aprobado || 0} aprobadas · {t.sugerido || 0} sugeridas · {t.desactivado || 0} desactivadas. Lo que aprueba una persona entra directo; lo que la IA envió sola queda sugerido hasta revisarlo.
      </Typography>
      <Box style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <TextField select size="small" variant="outlined" label="Estado" value={status} onChange={(e) => setStatus(e.target.value)} style={{ minWidth: 200 }}>
          {STATUS_OPTIONS.map(([v, l]) => <MenuItem key={v || "all"} value={v}>{l}</MenuItem>)}
        </TextField>
        <TextField size="small" variant="outlined" label="Buscar" value={search} onChange={(e) => setSearch(e.target.value)} style={{ minWidth: 200, flex: 1 }} />
        <FormControlLabel control={<Switch checked={review} onChange={(e) => setReview(e.target.checked)} color="primary" />} label="Para revisar (posible dato personal)" />
        {master && <Button variant="contained" color="primary" onClick={() => setEdit(null)}>Nueva entrada</Button>}
      </Box>
      {!data ? <CircularProgress /> : data.entries.length === 0 ? (
        <Typography color="textSecondary">El banco está vacío para este filtro.</Typography>
      ) : (
        data.entries.map((e) => (
          <Box key={e.id} data-knowledge={e.id} style={{ border: "1px solid #e3e8eb", borderLeft: `4px solid ${ST[e.status]}`, borderRadius: 6, padding: "8px 12px", marginBottom: 8 }}>
            <Box style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <Typography variant="caption" style={{ fontFamily: "monospace", fontWeight: 700 }}>banco#{e.id}</Typography>
              <Chip size="small" label={e.status} style={{ background: ST[e.status], color: "#fff" }} />
              <Chip size="small" variant="outlined" label={`Tema: ${e.topic}`} />
              <Chip size="small" variant="outlined" label={e.language} />
              {e.requiresReview && <Chip size="small" label="Revisar: posible dato personal" style={{ background: "#ff6f00", color: "#fff" }} />}
              <Typography variant="caption" color="textSecondary">usos {e.uses} · {e.origin || "—"} · {fmtDateTime(e.updatedAt)}</Typography>
              {e.sourceRequestId && (
                <Button size="small" onClick={() => onOpenRequest(e.sourceRequestId)}>Consulta #{e.sourceRequestId}</Button>
              )}
            </Box>
            <Typography variant="subtitle2" style={{ marginTop: 4 }}>{e.title}</Typography>
            {e.query && <Typography variant="caption" color="textSecondary" component="div">Consulta: {e.query}</Typography>}
            <Clamp text={e.answer} lines={3} />
            {master && (
              <Box style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                <Button size="small" variant="outlined" onClick={() => setEdit(e)}>Editar</Button>
                {e.status === "sugerido" && <Button size="small" variant="outlined" style={{ color: "#2e7d32", borderColor: "#2e7d32" }} onClick={() => setEntryStatus(e, "aprobado")}>Aprobar</Button>}
                {e.status !== "desactivado"
                  ? <Button size="small" variant="outlined" style={{ color: "#757575" }} onClick={() => setEntryStatus(e, "desactivado")}>Desactivar</Button>
                  : <Button size="small" variant="outlined" onClick={() => setEntryStatus(e, "aprobado")}>Reactivar</Button>}
              </Box>
            )}
          </Box>
        ))
      )}
      {data?.hasMore && <Button onClick={() => load(page + 1)}>Ver más</Button>}
      {edit !== undefined && <EditDialog entry={edit} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); load(1); }} />}
    </Box>
  );
};

export default KnowledgeTab;
